import { useState, useEffect, useRef } from 'react';
import { MicrophoneIcon } from '@heroicons/react/24/outline';
import * as Toast from '~/lib/Toast';
import useBusinessPostApi from '~/lib/useBusinessPostApi';

const AssistantVoiceInput = ({ prompt, setPrompt, onStartListening }) => {
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef(null);
    const initialPromptRef = useRef(prompt);
    const { save: trackVoiceInput } = useBusinessPostApi('/assistant/voice-input-started', { noToast: true });

    useEffect(() => {
        // Check if Web Speech API is available
        const SpeechRecognition =
            window.SpeechRecognition || window.webkitSpeechRecognition;
        setIsSupported(!!SpeechRecognition);

        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = event => {
                let finalTranscript = '';
                let interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript;
                    } else {
                        interimTranscript += transcript;
                    }
                }

                // Update prompt with final transcript, or interim if no final yet
                if (finalTranscript) {
                    const newPrompt = initialPromptRef.current ? `${initialPromptRef.current} ${finalTranscript}` : finalTranscript;
                    setPrompt(newPrompt);
                    initialPromptRef.current = newPrompt;
                } else if (interimTranscript) {
                    setPrompt(prev =>
                        initialPromptRef.current ? `${initialPromptRef.current} ${interimTranscript}` : interimTranscript,
                    );
                }
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognition.onerror = event => {
                console.error('Speech recognition error:', event.error);
                Toast.error('Speech recognition failed');
                setIsListening(false);
            };

            recognitionRef.current = recognition;
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, [setPrompt]);

    const toggleListening = () => {
        if (!recognitionRef.current) return;

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            try {
                recognitionRef.current.start();
                setIsListening(true);
                initialPromptRef.current = prompt;
                onStartListening();
                // Track voice input started event
                trackVoiceInput({});
            } catch (error) {
                console.error('Failed to start voice recognition:', error);
                Toast.error('Failed to start voice recognition');
            }
        }
    };

    // Don't render if Web Speech API is not supported
    if (!isSupported) {
        return null;
    }

    return (
        <button
            type="button"
            onClick={toggleListening}
            className={`p-2 rounded-lg transition-colors mr-1 ${
                isListening
                    ? 'text-white bg-primary-700'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
            title={isListening ? 'Stop listening' : 'Start voice input'}
        >
            <MicrophoneIcon
                className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`}
            />
        </button>
    );
};

export default AssistantVoiceInput;
